import React, { useState } from 'react';
import Modal from '$/components/global/modal';
import BixButton from '$/components/global/bixButton';
import { PlannerTranscriptionService, TranscriptionsAdminService } from '$/services';
import * as Bixby from '$/core';
import dayjs from 'dayjs';

export interface IDeleteTranscriptionModalProps {
  transcription: Bixby.Models.AdminCompanyTranscription | undefined;
  isOpen: boolean;
  onClose: () => void;
  onDeleted: () => void;
  dispatchToastError?: (message: string) => Promise<void>;
  dispatchToastSuccess?: (message: string) => Promise<void>;
}

const DeleteTranscriptionModal: React.FC<IDeleteTranscriptionModalProps> = ({
  transcription,
  isOpen,
  onClose,
  onDeleted,
  dispatchToastError,
  dispatchToastSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  if (!transcription) return null;

  const handleDelete = async () => {
    setError(undefined);
    setIsLoading(true);

    try {
      const result = await TranscriptionsAdminService.deleteTranscription(transcription.transcriptionId);

      if (result.newsExists && result.newTranscriptionId) {
        // News still exists — create a re-upload Planner task using the existing create flow.
        // All transcription details (including the assignee) come from the delete result returned by the backend.
        try {
          const transcriptionDetails: Bixby.Models.NewsTranscriptionDetails = {
            assignees: result.assigneeId ? [result.assigneeId] : [],
            bixbyId: result.companyBixbyId ?? '',
            client_Id: result.clientId ?? '',
            client_Name: result.clientName ?? '',
            client_DataSiteEmail: result.clientDataSiteEmail ?? '',
            company_Id: result.companyId ?? '',
            companyTranscription_Id: result.newTranscriptionId,
            plannerTranscriptionCreateId: '',
            replayGoodFrom: result.replayGoodFrom ? dayjs(result.replayGoodFrom).format('MM/DD/YYYY') : '',
            replayGoodUntil: result.replayGoodUntil ? dayjs(result.replayGoodUntil).format('MM/DD/YYYY') : '',
            replayPhoneNumber: result.replayPhoneNumber ?? '',
            replayPin: result.replayPin ?? '',
            replayStreamingUrl: result.replayStreamingUrl ?? '',
            replayType: result.replayType ?? Bixby.Enums.ReplayType.Phone,
            hasPrivateDeclaration: result.hasPrivateDeclaration ?? false,
          };

          const plannerTaskId = await PlannerTranscriptionService.addTranscriptionCreateTaskAsync(transcriptionDetails);
          await TranscriptionsAdminService.savePlannerCreateTask(result.newTranscriptionId, plannerTaskId);
        } catch {
          setError('An error occurred while creating planner task. Please try again.');
          if (dispatchToastError) {
            await dispatchToastError('Could not create planner task.');
            return;
          }
        }

        if (dispatchToastSuccess) {
          await dispatchToastSuccess('Transcript deleted. A new upload task has been created.');
        }
      } else {
        if (dispatchToastSuccess) {
          await dispatchToastSuccess('Transcript deleted.');
        }
      }

      onClose();
      onDeleted();
    } catch {
      setError('An error occurred while deleting the transcript. Please try again.');
      if (dispatchToastError) {
        await dispatchToastError('Could not delete transcript.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(undefined);
      onClose();
    }
  };

  return (
    <Modal title="Delete Transcript" active={isOpen} onClose={handleClose}>
      <p>
        Are you sure you want to delete the transcript for <strong>{transcription.companyName}</strong>?
      </p>
      {error && <p className="error">{error}</p>}
      <div className="button_side_wrap">
        <BixButton type="button" onClick={handleDelete} disabled={isLoading} useLoading isLoading={isLoading}>
          Delete Transcript
        </BixButton>
        <BixButton light type="button" onClick={handleClose} disabled={isLoading}>
          Cancel
        </BixButton>
      </div>
    </Modal>
  );
};

export default DeleteTranscriptionModal;
